import { Tooltip } from "antd";

import { GauInText } from "core/billing";

type SegmentedUsageBarProps = {
  /** Total GAUs used this cycle (plan-pool used + top-up used). */
  used: number;
  /** Total GAUs available this cycle (plan-pool purchased + top-up purchased). */
  total: number;
  /**
   * Optional: total top-up GAUs purchased this cycle. Defaults to 0.
   * When > 0, the bar renders 4 segments (plan-used, plan-unused, topup-used,
   * topup-unused). When 0, the bar renders the existing single-color shape.
   */
  topupPurchased?: number;
  /** Optional: top-up GAUs already consumed this cycle. Defaults to 0. */
  topupUsed?: number;
  /** Tailwind height class (default `h-[5px]` to match the modal panel). */
  heightClassName?: string;
  /** Tailwind border-radius class (default `rounded-[32px]`). */
  radiusClassName?: string;
};

const PLAN_USED = "#0099ff"; // unchanged from original bar
const PLAN_UNUSED = "#b3e0ff"; // light tint of #0099ff (only visible mid-bar)
const TOPUP_USED = "#10b981"; // emerald-500
const TOPUP_UNUSED = "#d1fae5"; // emerald-100 — very light, used for the trailing tail
const TRACK_GREY = "#f3f4f6"; // matches today's bar track when no top-up exists

/**
 * Decompose `used` / `total` into four buckets and return their ratios.
 * Exported so callers can derive identical numbers (e.g. for a "(N + M)"
 * breakdown label) without re-doing the arithmetic.
 */
export function deriveUsageBuckets({
  used,
  total,
  topupPurchased = 0,
  topupUsed = 0,
}: Pick<
  SegmentedUsageBarProps,
  "used" | "total" | "topupPurchased" | "topupUsed"
>) {
  const safeTopupUsed = Math.max(0, Math.min(topupUsed, topupPurchased));
  const safeTopupUnused = Math.max(0, topupPurchased - safeTopupUsed);
  const planTotal = Math.max(0, total - topupPurchased);
  const planUsed = Math.max(0, used - safeTopupUsed);
  const planUsedClamped = Math.min(planUsed, planTotal);
  const planUnused = Math.max(0, planTotal - planUsedClamped);
  return {
    planTotal,
    planUsed: planUsedClamped,
    planUnused,
    topupPurchased,
    topupUsed: safeTopupUsed,
    topupUnused: safeTopupUnused,
  };
}

export function SegmentedUsageBar({
  used,
  total,
  topupPurchased = 0,
  topupUsed = 0,
  heightClassName = "h-[5px]",
  radiusClassName = "rounded-[32px]",
}: SegmentedUsageBarProps) {
  const hasTopup = topupPurchased > 0 || topupUsed > 0;
  const buckets = deriveUsageBuckets({
    used,
    total,
    topupPurchased,
    topupUsed,
  });
  const denom =
    buckets.planUsed +
    buckets.planUnused +
    buckets.topupUsed +
    buckets.topupUnused;
  const pct = (n: number) => (denom > 0 ? (n / denom) * 100 : 0);

  return (
    <div
      className={`flex w-full overflow-hidden ${heightClassName} ${radiusClassName}`}
      style={{ background: TRACK_GREY }}
    >
      {buckets.planUsed > 0 ? (
        <Tooltip
          title={
            <GauInText>{`Plan pool used · ${Math.round(buckets.planUsed).toLocaleString()} GAUs`}</GauInText>
          }
        >
          <div
            data-test="usage-bar-plan-used"
            className="h-full transition-all"
            style={{
              width: `${pct(buckets.planUsed)}%`,
              background: PLAN_USED,
            }}
          />
        </Tooltip>
      ) : null}
      {buckets.planUnused > 0 ? (
        <Tooltip
          title={
            <GauInText>{`Plan pool remaining · ${Math.round(buckets.planUnused).toLocaleString()} GAUs`}</GauInText>
          }
        >
          <div
            data-test="usage-bar-plan-unused"
            className="h-full transition-all"
            style={{
              width: `${pct(buckets.planUnused)}%`,
              // When no top-up exists, the trailing plan-unused segment uses
              // the existing grey track color so the bar looks identical to
              // today. With a top-up present, plan-unused sits in the middle
              // and uses the lighter blue tint instead.
              background: hasTopup ? PLAN_UNUSED : TRACK_GREY,
            }}
          />
        </Tooltip>
      ) : null}
      {buckets.topupUsed > 0 ? (
        <Tooltip
          title={
            <GauInText>{`Top up used · ${Math.round(buckets.topupUsed).toLocaleString()} GAUs`}</GauInText>
          }
        >
          <div
            data-test="usage-bar-topup-used"
            className="h-full transition-all"
            style={{
              width: `${pct(buckets.topupUsed)}%`,
              background: TOPUP_USED,
            }}
          />
        </Tooltip>
      ) : null}
      {buckets.topupUnused > 0 ? (
        <Tooltip
          title={
            <GauInText>{`Top up remaining · ${Math.round(buckets.topupUnused).toLocaleString()} GAUs`}</GauInText>
          }
        >
          <div
            data-test="usage-bar-topup-unused"
            className="h-full transition-all"
            style={{
              width: `${pct(buckets.topupUnused)}%`,
              background: TOPUP_UNUSED,
            }}
          />
        </Tooltip>
      ) : null}
    </div>
  );
}
